import { createSystem, defaultConfig, defineConfig } from '@chakra-ui/react'

const customConfig = defineConfig({
  globalCss: {
    ':root, .light': {
      '--bg-app': '247 247 245',
      '--bg-surface': '255 255 255',
      '--bg-surface-2': '240 240 238',
      '--bg-sidebar': '236 236 234',
      '--fg': '26 26 31',
      '--fg-muted': '74 74 85',
      '--fg-subtle': '120 120 132',
      '--fg-faint': '168 168 178',
      '--border': '210 210 215',
      '--border-strong': '170 170 178',
      '--brand': '76 175 175',
      '--brand-fg': '255 255 255',
      '--accent-bg': '76 175 175',
      '--overlay': '0 0 0',
      '--chart-grid': '210 210 215',
      colorScheme: 'light',
    },
    '.dark': {
      '--bg-app': '10 10 18',
      '--bg-surface': '26 26 46',
      '--bg-surface-2': '37 37 56',
      '--bg-sidebar': '23 23 23',
      '--fg': '245 245 250',
      '--fg-muted': '196 196 204',
      '--fg-subtle': '148 148 160',
      '--fg-faint': '100 100 115',
      '--border': '60 60 80',
      '--border-strong': '90 90 115',
      '--brand': '76 175 175',
      '--brand-fg': '10 10 18',
      '--accent-bg': '76 175 175',
      '--overlay': '0 0 0',
      '--chart-grid': '50 50 70',
      colorScheme: 'dark',
    },
    'html, body': {
      bg: 'bg.app',
      color: 'fg',
      minHeight: '100vh',
    },
    '#root': {
      minHeight: '100vh',
    },
    '::-webkit-scrollbar': {
      width: '8px',
      height: '8px',
    },
    '::-webkit-scrollbar-track': {
      bg: 'transparent',
    },
    '::-webkit-scrollbar-thumb': {
      bg: 'fg.faint',
      borderRadius: 'full',
    },
    '::-webkit-scrollbar-thumb:hover': {
      bg: 'fg.subtle',
    },
  },
  theme: {
    tokens: {
      colors: {
        brand: {
          50:  { value: '#e6fffa' },
          100: { value: '#b2f5ea' },
          200: { value: '#81e6d9' },
          300: { value: '#4fd1c5' },
          400: { value: '#38b2ac' },
          500: { value: '#4CAFAF' },
          600: { value: '#2c7a7b' },
          700: { value: '#285e61' },
          800: { value: '#234e52' },
          900: { value: '#1d4044' },
        },
      },
      fonts: {
        heading: { value: `'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Helvetica, Arial, sans-serif` },
        body:    { value: `'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Helvetica, Arial, sans-serif` },
        mono:    { value: `'JetBrains Mono', 'Fira Code', monospace` },
      },
    },
    semanticTokens: {
      colors: {
        'bg.app':      { value: 'rgb(var(--bg-app))' },
        'bg.surface':  { value: 'rgb(var(--bg-surface))' },
        'bg.surface-2':{ value: 'rgb(var(--bg-surface-2))' },
        'bg.sidebar':  { value: 'rgb(var(--bg-sidebar))' },
        fg:            { value: 'rgb(var(--fg))' },
        'fg.muted':    { value: 'rgb(var(--fg-muted))' },
        'fg.subtle':   { value: 'rgb(var(--fg-subtle))' },
        'fg.faint':    { value: 'rgb(var(--fg-faint))' },
        border:        { value: 'rgb(var(--border))' },
        'border.strong':{ value: 'rgb(var(--border-strong))' },
        'accent.bg':   { value: 'rgb(var(--accent-bg))' },
        // Info-banner tokens — tinted in both light and dark mode
        'accent.subtle.bg':     { value: 'color-mix(in srgb, rgb(var(--accent-bg)) 10%, rgb(var(--bg-surface)))' },
        'accent.subtle.border': { value: 'color-mix(in srgb, rgb(var(--accent-bg)) 35%, rgb(var(--border)))' },
        'info.subtle.bg':       { value: 'color-mix(in srgb, rgb(59 130 246) 10%, rgb(var(--bg-surface)))' },
        'info.subtle.border':   { value: 'color-mix(in srgb, rgb(59 130 246) 35%, rgb(var(--border)))' },
        // Tooltip surface — softer than pure fg, contrasts well in both modes
        'tooltip.bg':           { value: 'rgb(var(--fg-muted))' },
        'tooltip.fg':           { value: 'rgb(var(--bg-surface))' },
        // Legacy aliases so un-migrated files still resolve
        'surface.dark':  { value: 'rgb(var(--bg-app))' },
        'surface.card':  { value: 'rgb(var(--bg-surface))' },
        'surface.hover': { value: 'rgb(var(--bg-surface-2))' },
        'sidebar.bg':    { value: 'rgb(var(--bg-sidebar))' },
        'sidebar.hover': { value: 'rgba(var(--fg), 0.04)' },
        'sidebar.active':{ value: 'color-mix(in srgb, rgb(var(--accent-bg)) 15%, transparent)' },
        'sidebar.border':{ value: 'rgba(76, 175, 175, 0.6)' },
      },
    },
  },
})

export const system = createSystem(defaultConfig, customConfig)
